import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import {
  getBuyButtonDisplayTypeForPaidVideo,
  getResizedImageUrl,
} from '@wix/wix-vod-shared/common';
import {
  StatisticLogger,
  PlaybackSuccessLogger,
  ActionsPanel,
  VideoCards,
} from '@wix/wix-vod-shared/components';
import { isVideoCard, isChannelCard } from '@wix/wix-vod-shared/widget';
import { hasEnabledDealInfo } from '../../../utils';
import { DEEPLINKS_REDIRECT_URL } from '../../../config';
import { PRODUCTION_HOSTS } from '@wix/wix-vod-constants/env';
import getBuyRentText from '../../../utils/get-buy-rent-text';
import getPlayerTranslations from '../../../utils/get-player-translations';
import isRTL from '../../../utils/is-rtl';
import ShareInfoOverlay, {
  INFO_OVERLAY_CONTENT,
} from '../../../components/share-info-overlay/share-info-overlay';
import Cover from '../../../components/cover/cover';
import { logBi, logSharedBi } from '../../../bi';
import i18n from '../../../i18n';
import { VodPlayerWithReceiver as VODPlayer } from '../wrap-with-receiver/wrap-with-receiver';
import styles from './styles.scss';
import { fedopsInteractions } from '../../../services/fedops-logger';

class Desktop extends React.Component {
  static propTypes = {
    dataHook: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    videoItem: PropTypes.object,
    channelData: PropTypes.object,
    settingsModuleConfig: PropTypes.object,
    videoCards: PropTypes.array,
    vidiUrls: PropTypes.array,
    siteUrl: PropTypes.string,
    posterUrl: PropTypes.string,
    instanceInfo: PropTypes.object,
    correlationId: PropTypes.string.isRequired,

    canShowTitle: PropTypes.bool,
    canShareVideo: PropTypes.bool,
    canShowFullInfo: PropTypes.bool,
    areElementsRounded: PropTypes.bool,
    logo: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    isVideoPlayRequested: PropTypes.bool,
    isLive: PropTypes.bool,
    isOverQuota: PropTypes.bool,

    onShareRequest: PropTypes.func,
    goToSite: PropTypes.func,
    goToSiteWithShare: PropTypes.func,
    goToSiteWithPurchase: PropTypes.func,
    onEnd: PropTypes.func,
  };

  static defaultProps = {
    isVideoPlayRequested: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      overlayContent: null,
      shouldSendStatistic: false,
      isVideoPlayRequested: props.isVideoPlayRequested,
      playHasBeenStarted: false,
      isRTLEnabled: isRTL(),
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isVideoPlayRequested) {
      this.setState({
        isVideoPlayRequested: nextProps.isVideoPlayRequested,
      });
    }
  }

  showVideoInfo = () => {
    logSharedBi('widget.vid.info.clicked');
    this.setState({
      overlayContent: INFO_OVERLAY_CONTENT.VIDEO_INFO,
    });
  };

  showShareInfo = () => {
    this.setState({
      overlayContent: INFO_OVERLAY_CONTENT.SHARE_VIDEO,
    });
  };

  showAllCards = () => {
    this.setState({
      overlayContent: INFO_OVERLAY_CONTENT.ALL_CARDS,
    });
  };

  closeOverlay = () => {
    this.setState({ overlayContent: null });
  };

  onPlayClick = () => {
    const { videoItem } = this.props;

    if (videoItem.memberOnly) {
      this.props.goToSite();
    } else {
      this.startPlaying();
    }
  };

  startPlaying = () => {
    const { isOverQuota, correlationId } = this.props;

    fedopsInteractions.playVideoDesktop.start();

    logBi('widget.embed.vid.play.clicked', {
      correlationId,
      videoType: 'computer',
      videoWidth: window.innerWidth,
      videoHeight: window.innerHeight,
      overQuoted: isOverQuota,
    });

    this.setState({ isVideoPlayRequested: true, playHasBeenStarted: false });
  };

  onPlayRequested = () => {
    const { isVideoPlayRequested } = this.state;

    if (!isVideoPlayRequested) {
      this.startPlaying();
    }
  };

  onEnd = () => {
    this.stopSendingStatistic();
    this.setState({
      isVideoPlayRequested: false,
      playHasBeenStarted: false,
      overlayContent: null,
    });

    if (this.props.onEnd) {
      this.props.onEnd();
    }
  };

  onBuyRentButtonClick = () => {
    this.sendBuyButtonClickedBi();
    this.props.goToSiteWithPurchase();
  };

  sendBuyButtonClickedBi = () => {
    logSharedBi('widget.vid.buy.clicked', {
      button: 'main',
      buyType: getBuyButtonDisplayTypeForPaidVideo(this.props.videoItem),
    });
  };

  canShowBuyRentButton(videoItem) {
    return hasEnabledDealInfo(videoItem.dealInfo);
  }

  canShowVideoCards(videoCards) {
    return !isEmpty(videoCards);
  }

  buyRentButtonText(videoItem, channelData) {
    return getBuyRentText(videoItem, channelData);
  }

  onTitleClick = () => {
    logBi('widget.embed.vid.goToSite.click', { buttonName: 'title' });
    this.props.goToSite();
  };

  onWatchOnSiteClick = () => {
    logBi('widget.embed.vid.goToSite.click', { buttonName: 'logo' });
    this.props.goToSite();
  };

  onMoreClick = () => {
    logBi('widget.embed.vid.goToSite.click', { buttonName: 'more' });
    this.props.goToSiteWithShare();
  };

  onPlayStart = () => {
    const { playHasBeenStarted } = this.state;
    const { correlationId } = this.props;
    this.startSendingStatistic();
    if (!playHasBeenStarted) {
      fedopsInteractions.playVideoDesktop.end();

      logBi('widget.embed.vid.start.play', {
        correlationId,
        videoType: 'computer',
        videoWidth: window.innerWidth,
        videoHeight: window.innerHeight,
      });
      /*
        In case when play was triggered by post message(from another app) we still
        want to hide overlay and run video, but now we have the question.
        Should we just call onPlayRequested(problem with BI) or we need just to toggle isVideoPlayRequested
      */
      this.setState({ playHasBeenStarted: true, isVideoPlayRequested: true });
    }
  };

  onFullScreenChange = (isInFullScreen) => {
    if (isInFullScreen) {
      logSharedBi('widget.vid.fullScreen.entered', {
        origin: 'embed',
      });
    }
  };

  onPlayerInit = (player) => {
    this.setState({ player });
  };

  onCardClick = (card) => {
    if (card.url) {
      return;
    }

    this.redirectToVideo(card);
  };

  redirectToVideo(card) {
    const { siteUrl } = this.props;
    const encodedUrl = encodeURIComponent(siteUrl);

    if (isVideoCard(card)) {
      window.open(
        `${DEEPLINKS_REDIRECT_URL}?list_id=${card.actionListId}&item_id=${card.actionItemId}&fallback_url=${encodedUrl}`,
        '_blank',
      );
    } else if (isChannelCard(card)) {
      const channelUrl = `${DEEPLINKS_REDIRECT_URL}?list_id=${card.actionListId}&fallback_url=${encodedUrl}`;
      window.open(channelUrl, '_blank');
    }
  }

  startSendingStatistic = () => {
    this.setState({
      shouldSendStatistic: true,
    });
  };

  stopSendingStatistic = () => {
    this.setState({
      shouldSendStatistic: false,
    });
  };

  logStatistic = (data) => {
    const { correlationId } = this.props;
    logBi('player.debug.info', { ...data, correlationId });
  };

  getActionButtonLabels() {
    return {
      videoInfoFullText: i18n.t('share-overlay.share.video-info-full'),
      videoInfoShortText: i18n.t('share-overlay.share.video-info-short'),
      shareVideoFullText: i18n.t('share-overlay.share.share-video-full'),
      shareVideoShortText: i18n.t('share-overlay.share.share-video-short'),
      showAllCardsFullText: i18n.t('share-overlay.share.video-cards-full'),
      showAllCardsShortText: i18n.t('share-overlay.share.video-cards-short'),
    };
  }

  logPlaybackTry(data) {
    logBi('player.play.requested', data);
  }

  logPlaybackSuccess(data) {
    logBi('player.play.done', data);
  }

  getVideoCardsAccessibilityLabels() {
    return {
      facebookButtonTitle: i18n.t(
        'accessibility.action-cards.facebook-like-button',
      ),
      youtubeButtonTitle: i18n.t(
        'accessibility.action-cards.youtube-subscribe-button',
      ),
      twitterButtonTitle: i18n.t(
        'accessibility.action-cards.twitter-follow-button',
      ),
    };
  }

  render() {
    const {
      overlayContent,
      isVideoPlayRequested,
      shouldSendStatistic,
      player,
      isRTLEnabled,
    } = this.state;

    const {
      instanceInfo,
      videoItem,
      channelData,
      videoCards,
      width,
      height,
      onShareRequest,
      siteUrl,
      posterUrl,

      areElementsRounded,
      canShowFullInfo,
      canShareVideo,
      isLive,
    } = this.props;

    const videoCardsAccessibilityLabels =
      this.getVideoCardsAccessibilityLabels();

    const playerPosterUrl = getResizedImageUrl({
      url: posterUrl,
      width,
      height,
      imageHost: PRODUCTION_HOSTS.image,
    });

    return (
      <div>
        {!isVideoPlayRequested && (
          <Cover
            posterUrl={posterUrl}
            canShowTitle
            videoItem={videoItem}
            channelData={channelData}
            title={videoItem.title}
            width={width}
            height={height}
            onBuyClicked={this.onBuyRentButtonClick}
            onPlayClicked={this.onPlayClick}
          />
        )}

        {VODPlayer && (
          <div
            aria-hidden={!isVideoPlayRequested}
            className={classNames({ [styles.hidden]: !isVideoPlayRequested })}
            dir="ltr"
          >
            <VODPlayer
              {...this.props}
              dataHook="internal-player"
              preload="none"
              isVideoPlayRequested={isVideoPlayRequested}
              onEnded={this.onEnd}
              texts={getPlayerTranslations()}
              onWatchOnSiteClick={this.onWatchOnSiteClick}
              onFullScreenChanged={this.onFullScreenChange}
              onPlay={this.onPlayStart}
              onPause={this.stopSendingStatistic}
              onPlayRequested={this.onPlayRequested}
              onTitleClick={this.onTitleClick}
              onInit={this.onPlayerInit}
              posterUrl={playerPosterUrl}
            >
              <ActionsPanel
                canFocus={isVideoPlayRequested}
                videoItem={videoItem}
                areElementsRounded={areElementsRounded}
                canShowFullInfo={canShowFullInfo}
                canShareVideo={canShareVideo}
                onVideoInfoShow={this.showVideoInfo}
                onShareVideoShow={this.showShareInfo}
                onAllCardsButtonClick={this.showAllCards}
                buyRentButtonText={this.buyRentButtonText(
                  videoItem,
                  channelData,
                )}
                onBuyRentButtonClick={this.onBuyRentButtonClick}
                canShowBuyRentButton={this.canShowBuyRentButton(videoItem)}
                canShowAllCardsButton={this.canShowVideoCards(videoCards)}
                infoButtonAriaLabel={i18n.t('accessibility.info-overlay')}
                shareButtonAriaLabel={i18n.t('accessibility.share-button')}
                allCardsButtonAriaLabel={i18n.t(
                  'accessibility.all-cards-button',
                )}
                {...this.getActionButtonLabels()}
              />

              <ShareInfoOverlay
                overlayContent={overlayContent}
                onClose={this.closeOverlay}
                videoItem={videoItem}
                channelData={channelData}
                videoCards={videoCards}
                onShareRequest={onShareRequest}
                onMoreClicked={this.onMoreClick}
                onCardClick={this.onCardClick}
                videoCardsAccessibilityLabels={videoCardsAccessibilityLabels}
                siteUrl={siteUrl}
                instanceInfo={instanceInfo}
                isRTL={isRTLEnabled}
              />

              {player && (
                <VideoCards
                  cardsData={videoCards}
                  player={player}
                  {...videoCardsAccessibilityLabels}
                  onCardClick={this.onCardClick}
                />
              )}
              {player && (
                <StatisticLogger
                  videoID={videoItem.id}
                  engine={player._defaultModules.engine}
                  isEmbed
                  isLive={isLive}
                  logFunction={this.logStatistic}
                  channelID={channelData.id}
                  shouldSendBI={shouldSendStatistic}
                />
              )}
              {player && (
                <PlaybackSuccessLogger
                  videoID={videoItem.id}
                  engine={player._defaultModules.engine}
                  eventEmitter={player._defaultModules.eventEmitter}
                  logTryFunction={this.logPlaybackTry}
                  logSuccessFunction={this.logPlaybackSuccess}
                  channelID={channelData.id}
                />
              )}
            </VODPlayer>
          </div>
        )}
      </div>
    );
  }
}

export default Desktop;
